import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import unescape from 'lodash/unescape';
import FocusLock from 'react-focus-lock';
import classNames from 'classnames';
import sanitizeHTML from 'sanitize-html';
import { featureMatchHubsFixWebtourScrollingOnChrome } from '../../../featureFlags';
import withHostContext, {
  trackingLocations,
  trackClick,
} from '../../context/host/consumer';
import { mediaTypes, scrollSize } from './constants';
import { isValidMedia, transformIframeUrl } from './utils';
import { isVisible } from '../../utils/isVisible';
import Button from '../Button';
import Icon from '../Icon';
import { ReactComponent as ImageSvg } from './image-regular.svg';
import './styles.css';

const STORAGE_KEY = 'webtourOverlay:dismissed';

// we use this for aria labels or alt values for screen readers
const sanitizeConfig = {
  allowedTags: [],
  allowedAttributes: {},
};

export class MediaCarousel extends React.Component {
  static propTypes = {
    productId: PropTypes.number,
    media: PropTypes.arrayOf(PropTypes.object),
    isEditMode: PropTypes.bool,
    featureFlags: PropTypes.object,
  };

  state = {
    openModal: false,
    overlayDismissed: true,
  };

  trackedTiles = [];

  constructor(props) {
    super(props);
    const ids = [];
    for (let i = 1; i <= 31; i += 1) {
      ids[i] = i.toString();
    }
    this.state.ids = ids;
    this.state.overlayDismissed = sessionStorage.getItem(STORAGE_KEY) === '1';
    if (this.props.productId) {
      trackClick(this.props.hostContext, {
        productId: this.props.productId,
        location: trackingLocations.COLLEGE_PROFILE_HEADER,
      });
      this.trackVisibleTiles = this.trackVisibleTiles.bind(this);
      window.addEventListener('resize', this.trackVisibleTiles);
      window.addEventListener('scroll', this.trackVisibleTiles);
    }
  }

  componentDidMount() {
    if (this.props.productId) {
      window.addEventListener('resize', this.trackVisibleTiles);
      window.addEventListener('scroll', this.trackVisibleTiles);
      this.trackVisibleTiles();
    }
  }

  slideWrapperLeft = () => {
    /* istanbul ignore else */
    if (this.imagesWrapperRef) {
      const { scrollLeft, scrollWidth, clientWidth } = this.imagesWrapperRef;
      const maxScroll = scrollWidth - clientWidth;
      let left = 0;
      if (scrollLeft + scrollSize > maxScroll) {
        left = maxScroll - scrollLeft;
      } else {
        left = scrollSize;
      }
      this.imagesWrapperRef.scroll({
        top: 0,
        left: scrollLeft + left,
        behavior: 'smooth',
      });
      this.fixChromeScrolling(scrollLeft, scrollLeft + left);
      this.trackVisibleTiles();
    }
  };

  slideWrapperRight = () => {
    /* istanbul ignore else */
    if (this.imagesWrapperRef) {
      const { scrollLeft } = this.imagesWrapperRef;
      let left = 0;
      if (scrollLeft - scrollSize < 0) {
        left = scrollLeft;
      } else {
        left = scrollSize;
      }
      this.imagesWrapperRef.scroll({
        top: 0,
        left: scrollLeft - left,
        behavior: 'smooth',
      });
      this.fixChromeScrolling(scrollLeft, scrollLeft - left);
      this.trackVisibleTiles();
    }
  };

  /* istanbul ignore next */
  fixChromeScrolling = (oldScrollLeft, newScrollLeft) => {
    // Fix for Chrome bug https://issues.chromium.org/issues/376097633
    if (this.props.featureFlags[featureMatchHubsFixWebtourScrollingOnChrome]) {
      setTimeout(() => {
        if (this.imagesWrapperRef.scrollLeft === oldScrollLeft) {
          this.imagesWrapperRef.scrollLeft = newScrollLeft;
        }
      }, 550);
    }
  };

  trackVisibleTiles = debounce(() => {
    if (this.props.productId && this.imagesWrapperRef) {
      const tiles = [].slice.call(
        this.imagesWrapperRef.querySelectorAll('.image-content'),
      );
      const visibleTiles = tiles
        .map((tile, index) => (isVisible(tile) ? index : -1))
        .filter((value) => value > -1);
      if (!isEmpty(visibleTiles)) {
        visibleTiles.forEach((tile) => {
          if (
            tiles[tile] &&
            tiles[tile].getAttribute('src') &&
            !this.trackedTiles.includes(tile)
          ) {
            this.trackedTiles.push(tile);
            trackClick(this.props.hostContext, {
              productId: this.props.productId,
              location: `${trackingLocations.WEB_TOUR_SCROLL}_${tiles[tile].getAttribute(
                'src',
              )}_${tile + 1}`,
            });
          }
        });
      }
    }
  }, 300);

  /* eslint-disable camelcase */
  mapMediaElement = (mediaElement, imageclassName = 'image-content') => {
    if (!isValidMedia(mediaElement)) return <div className="mock-image" />;
    switch (mediaElement.media_type) {
      case mediaTypes.VIDEO_URL: {
        if (imageclassName === 'modal-image') {
          return (
            <iframe
              className="video-iframe"
              title={sanitizeHTML(unescape(mediaElement.title), sanitizeConfig)}
              src={transformIframeUrl(mediaElement.media_url)}
              name={sanitizeHTML(unescape(mediaElement.title), sanitizeConfig)}
              frameBorder="0"
              data-autofocus
            />
          );
        } else {
          return (
            <div
              className={classNames(imageclassName, 'video')}
              style={{ backgroundImage: `url("${encodeURI(mediaElement.thumbnail)}")` }}
              tabIndex="0"
              data-autofocus
              aria-label={`Video: ${sanitizeHTML(
                unescape(mediaElement.title),
                sanitizeConfig,
              )}`}
            >
              <Icon name="youtube play" size="huge" className="play-button" />
            </div>
          );
        }
      }
      case mediaTypes.IMAGE_URL:
      case mediaTypes.IMAGE_S3: {
        const imageData = mediaElement.file || mediaElement.media_url;
        return (
          <img
            src={encodeURI(imageData)}
            className={imageclassName}
            tabIndex="0"
            data-autofocus
            alt={`${sanitizeHTML(unescape(mediaElement.title), sanitizeConfig)}`}
          />
        );
      }
      /* istanbul ignore next */
      default:
        return <div className="mock-image" />;
    }
  };
  /* eslint-enable camelcase */

  mapMediaChunk = (mediaChunk, index) => {
    const { ids } = this.state;

    /* eslint-disable jsx-a11y/click-events-have-key-events */
    const smallImage = (chunkPiece, offset) => {
      const id = ids[index * 2 + offset];
      return (
        <div className="small-image-container" aria-label={`tile number ${+id + 1}`}>
          <div
            name="media-carousel-small-photo"
            role="button"
            className={classNames('small-image', { top: offset === 1 })}
            id={id}
            onClick={(e) => this.handleTileClick(e, chunkPiece, id)}
          >
            {this.mapMediaElement(chunkPiece)}
          </div>
        </div>
      );
    };

    return (
      <div key={index} className="small-images-container">
        {smallImage(mediaChunk[0], 1)}
        {smallImage(mediaChunk[1], 2)}
      </div>
    );
  };

  handleOverlayClick = (e) => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    this.setState({ overlayDismissed: true });
  };

  handleTileClick = (e, mediaTile, targetIndex) => {
    if (this.props.productId) {
      trackClick(this.props.hostContext, {
        productId: this.props.productId,
        location: `${trackingLocations.WEB_TOUR_TILE}_${mediaTile.media_url}_${
          Number(targetIndex) + 1
        }`,
      });
    }
    this.setState({ openModal: true, selectedTile: { id: Number(targetIndex) } });
  };

  handleCaptionClick = (e, tileId) => {
    if (this.props.productId && this.imagesWrapperRef) {
      const tiles = [].slice.call(
        this.imagesWrapperRef.querySelectorAll('.image-content'),
      );
      if (get(e, 'target.href', null)) {
        trackClick(this.props.hostContext, {
          productId: this.props.productId,
          location: `${trackingLocations.WEB_TOUR_CAPTION}_${tiles[tileId].getAttribute(
            'src',
          )}_${Number(tileId) + 1}_${e.target.href}`,
        });
      }
    }
  };

  onKeyPressModal = (event) => {
    /* istanbul ignore else */
    if (event.key === 'Enter') {
      const { className } = event.target;
      /* istanbul ignore else */
      if (className.includes('close')) {
        this.setState({ openModal: false });
      } else if (className.includes('left')) {
        this.previousImage();
      } else if (className.includes('right')) {
        this.nextImage();
      }
      event.preventDefault();
    }
  };

  handleImagesWrapperRef = (el) => {
    this.imagesWrapperRef = el;
  };

  closeModal = () => {
    this.setState({ openModal: false });
  };

  previousImage = () => {
    const { selectedTile } = this.state;
    const id =
      selectedTile && selectedTile.id
        ? String(Number(selectedTile.id) - 1) /* istanbul ignore next */
        : '0';
    this.setState({ openModal: true, selectedTile: { id } });
  };

  nextImage = () => {
    const { selectedTile } = this.state;
    const id =
      selectedTile && 'id' in selectedTile
        ? String(Number(selectedTile.id) + 1) /* istanbul ignore next */
        : '0';
    this.setState({ openModal: true, selectedTile: { id } });
  };

  render() {
    /* istanbul ignore next */
    const { isEditMode } = this.props;
    const { selectedTile, openModal, overlayDismissed } = this.state;
    const unsortedMedia = this.props.media || [];
    const media = unsortedMedia.sort(
      (asset1, asset2) => Number(asset1.order) - Number(asset2.order),
    );
    const chunks = chunk(media.slice(1), 2);
    const overlayBckgndClass =
      media && media.length > 0 ? 'edit-mode-overlay-background' : '';

    const selectedTileId =
      selectedTile && selectedTile.id
        ? Number(selectedTile.id) /* istanbul ignore next */
        : 0;

    return isEditMode || (media && media.length > 0) ? (
      <div data-cy="media-carousel-main" className="images-overlay">
        <div className="images-wrapper" ref={this.handleImagesWrapperRef}>
          <div className="big-image-container" aria-label="tile number 1">
            <div
              name="media-carousel-small-photo"
              role="button"
              className="big-image-wrapper"
              id={0}
              onClick={(e) => this.handleTileClick(e, get(media, '[0]'), 0)}
            >
              {this.mapMediaElement(get(media, '[0]'))}
            </div>
          </div>
          {chunks.map(this.mapMediaChunk)}
        </div>
        {media && media.length > 0 ? (
          <React.Fragment>
            <Button
              onClick={this.slideWrapperLeft}
              data-cy="right-button"
              className="right-button"
              positive
              icon="chevron right"
              size="massive"
              aria-label="Right button"
            />
            <Button
              onClick={this.slideWrapperRight}
              data-cy="left-button"
              className="left-button"
              positive
              icon="chevron left"
              size="massive"
              aria-label="Left button"
            />
          </React.Fragment>
        ) : null}
        {isEditMode && !overlayDismissed && (
          <div className={classNames('edit-mode-overlay', overlayBckgndClass)}>
            <div className="edit-mode-overlay-msg">
              <ImageSvg className="edit-mode-overlay-img" />
              <p>
                Use the&nbsp;<i>Photos and Videos</i>&nbsp;option under the&nbsp;
                <i>Media</i>&nbsp;tab to manage your institution's gallery!
              </p>
              <div className="edit-mode-overlay-close" onClick={this.handleOverlayClick}>
                <Icon name="delete" size="small" />
              </div>
            </div>
          </div>
        )}
        {openModal && (
          <FocusLock returnFocus>
            <div className="modal-blur" />
            <div data-cy="media-carousel-modal" className="modal-lightbox">
              <div className="modal-close-button">
                <Icon
                  name="close"
                  aria-label="Close"
                  onClick={this.closeModal}
                  onKeyPress={this.onKeyPressModal}
                  tabIndex="0"
                  role="button"
                />
              </div>
              <div className="modal-inner">
                <div className="modal-inner-viewer">
                  <div
                    name="media-carousel-small-photo-modal"
                    role="button"
                    className="modal-image"
                    id={`modal-item-${selectedTileId}`}
                  >
                    {this.mapMediaElement(media[selectedTileId], 'modal-image')}
                  </div>
                </div>
                {selectedTileId > 0 && (
                  <div className="modal-button left">
                    <Icon
                      name="angle left"
                      aria-label="Previous image or video"
                      onClick={this.previousImage}
                      onKeyPress={this.onKeyPressModal}
                      tabIndex="0"
                      role="button"
                    />
                  </div>
                )}
                {!isEmpty(media[selectedTileId + 1]) && (
                  <div className="modal-button right">
                    <Icon
                      name="angle right"
                      aria-label="Next image or video"
                      onClick={this.nextImage}
                      onKeyPress={this.onKeyPressModal}
                      tabIndex="0"
                      role="button"
                    />
                  </div>
                )}
                {media[selectedTileId] && media[selectedTileId].caption && (
                  <div
                    aria-label={sanitizeHTML(
                      unescape(media[selectedTileId].caption),
                      sanitizeConfig,
                    )}
                    className="caption-in-modal"
                    tabIndex="0"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: unescape(media[selectedTileId].caption),
                    }}
                    onClick={(e) => this.handleCaptionClick(e, selectedTileId)}
                  />
                )}
              </div>
            </div>
          </FocusLock>
        )}
      </div>
    ) : null;
  }
}
/* eslint-enable jsx-a11y/click-events-have-key-events */

export default withHostContext(MediaCarousel);
